import React from "react";
import { hydrateRoot } from "react-dom/client";
import { PageShell } from "./PageShell";
import type { PageContext } from "./types";
import { AnimationProvider } from "../components/AnimationProvider";

// Update document metadata
function updateDocumentMetadata(pageContext: PageContext) {
  const { documentProps } = pageContext;
  if (!documentProps) return;

  const {
    title,
    description,
    keywords,
    author,
    canonicalUrl,
    ogTitle,
    ogDescription,
    ogImage,
    ogUrl,
  } = documentProps;

  // Update basic metadata
  if (title) document.title = title;
  if (description)
    document
      .querySelector('meta[name="description"]')
      ?.setAttribute("content", description);
  if (keywords)
    document
      .querySelector('meta[name="keywords"]')
      ?.setAttribute("content", keywords);
  if (author)
    document
      .querySelector('meta[name="author"]')
      ?.setAttribute("content", author);
  if (canonicalUrl)
    document
      .querySelector('link[rel="canonical"]')
      ?.setAttribute("href", canonicalUrl);

  // Update Open Graph metadata
  if (ogTitle)
    document
      .querySelector('meta[property="og:title"]')
      ?.setAttribute("content", ogTitle);
  if (ogDescription)
    document
      .querySelector('meta[property="og:description"]')
      ?.setAttribute("content", ogDescription);
  if (ogImage)
    document
      .querySelector('meta[property="og:image"]')
      ?.setAttribute("content", ogImage);
  if (ogUrl)
    document
      .querySelector('meta[property="og:url"]')
      ?.setAttribute("content", ogUrl);
}

// This runs in the browser only
export async function render(pageContext: PageContext) {
  const { Page, pageProps } = pageContext;

  // Update metadata on each page navigation
  updateDocumentMetadata(pageContext);

  const app = document.getElementById("app");
  if (!app) return;

  // Initial render without AnimationProvider
  const root = hydrateRoot(
    app,
    <React.StrictMode>
      <PageShell pageContext={pageContext}>
        <Page {...pageProps} />
      </PageShell>
    </React.StrictMode>,
  );

  // Add AnimationProvider after initial hydration
  setTimeout(() => {
    root.render(
      <React.StrictMode>
        <PageShell pageContext={pageContext}>
          <Page {...pageProps} />
        </PageShell>
        <AnimationProvider />
      </React.StrictMode>,
    );
  }, 0);
}
